import {RegionModifier} from "@/ui/timeline/tracks/audio-unit/regions/RegionModifier.ts"
import {BoxEditing} from "@opendaw/lib-box"
import {int, Option} from "@opendaw/lib-std"
import {ppqn, PPQN, RegionCollection} from "@opendaw/lib-dsp"
import {
    AnyLoopableRegionBoxAdapter,
    AnyRegionBoxAdapter,
    UnionAdapterTypes
} from "@opendaw/studio-adapters"
import {Snapping} from "@/ui/timeline/Snapping.ts"
import {RegionModifyStrategy} from "@opendaw/studio-core"
import {Dragging} from "@opendaw/lib-dom"

class SelectedModifyStrategy implements RegionModifyStrategy {
    readonly #tool: RegionContentStartModifier

    constructor(tool: RegionContentStartModifier) {this.#tool = tool}

    readPosition(region: AnyRegionBoxAdapter): ppqn {
        return region.position + this.#computeDelta(region)
    }
    readComplete(region: AnyRegionBoxAdapter): ppqn {return region.complete}
    readLoopOffset(region: AnyLoopableRegionBoxAdapter): ppqn {
        // just for the preview. it behaves like having an effect-offset
        return region.loopOffset + this.#computeDelta(region)
    }
    readLoopDuration(region: AnyLoopableRegionBoxAdapter): ppqn {
        return region.loopDuration - this.#computeDelta(region)
    }
    readMirror(region: AnyRegionBoxAdapter): boolean {return region.isMirrowed}
    translateTrackIndex(value: int): int {return value}
    iterateRange<R extends AnyRegionBoxAdapter>(regions: RegionCollection<R>, from: ppqn, to: ppqn): Iterable<R> {
        return regions.iterateRange(from, this.#tool.adapters.reduce((to, adapter) => Math.max(to, adapter.complete), to))
    }

    #computeDelta(region: AnyRegionBoxAdapter): ppqn {
        if (!UnionAdapterTypes.isLoopableRegion(region) || !region.canResize) {return 0}
        return this.#tool.delta
    }
}

type Construct = Readonly<{
    element: Element
    snapping: Snapping
    pointerPulse: ppqn
    reference: AnyRegionBoxAdapter
}>

export class RegionContentStartModifier implements RegionModifier {
    static create(selected: ReadonlyArray<AnyRegionBoxAdapter>, construct: Construct): Option<RegionContentStartModifier> {
        const adapters = selected.filter(region => UnionAdapterTypes.isLoopableRegion(region) && region.canResize)
        return adapters.length === 0 ? Option.None : Option.wrap(new RegionContentStartModifier(construct, adapters))
    }

    readonly #element: Element
    readonly #snapping: Snapping
    readonly #pointerPulse: ppqn
    readonly #reference: AnyRegionBoxAdapter
    readonly #adapters: ReadonlyArray<AnyLoopableRegionBoxAdapter>
    readonly #selectedModifyStrategy: SelectedModifyStrategy

    #delta: ppqn

    private constructor({element, snapping, pointerPulse, reference}: Construct,
                        adapters: ReadonlyArray<AnyLoopableRegionBoxAdapter>) {
        this.#element = element
        this.#snapping = snapping
        this.#pointerPulse = pointerPulse
        this.#reference = reference
        this.#adapters = adapters
        this.#selectedModifyStrategy = new SelectedModifyStrategy(this)

        this.#delta = 0
    }

    get delta(): ppqn {return this.#delta}
    get snapping(): Snapping {return this.#snapping}
    get adapters(): ReadonlyArray<AnyLoopableRegionBoxAdapter> {return this.#adapters}
    get reference(): AnyRegionBoxAdapter {return this.#reference}

    showOrigin(): boolean {return false}
    selectedModifyStrategy(): RegionModifyStrategy {return this.#selectedModifyStrategy}
    unselectedModifyStrategy(): RegionModifyStrategy {return RegionModifyStrategy.Identity}

    update({clientX}: Dragging.Event): void {
        const clientRect = this.#element.getBoundingClientRect()
        const newDelta = this.#snapping.computeDelta(
            this.#pointerPulse, clientX - clientRect.left, this.#reference.position)
        const clampedDelta = Math.min(newDelta, this.#computeMaxDelta())
        if (this.#delta !== clampedDelta) {
            this.#delta = clampedDelta
            this.#dispatchChange()
        }
    }

    #computeMaxDelta(): ppqn {
        return this.#adapters.reduce((maxDelta, adapter) => {
            const maxByDuration = adapter.duration - PPQN.SemiQuaver
            const maxByLoopDuration = adapter.loopDuration - PPQN.SemiQuaver
            return Math.min(maxDelta, maxByDuration, maxByLoopDuration)
        }, Infinity)
    }

    approve(editing: BoxEditing): void {
        if (this.#delta === 0) {return}
        editing.modify(() => this.#adapters.forEach(region => region.moveContentStart(this.#delta)))
    }

    cancel(): void {
        this.#delta = 0
        this.#dispatchChange()
    }

    toString(): string {return `RegionContentStartModifier{delta: ${this.#delta}}`}

    #dispatchChange(): void {
        this.#adapters.forEach(adapter => adapter.trackBoxAdapter
            .ifSome(trackAdapter => trackAdapter.regions.dispatchChange()))
    }
}